import { formatRangeValues } from 'lib/reportUtils';
import { ReportViewModel, Column, Amounts } from './types';
import {
  ChartDataColumn,
  ChartObject,
  ChartProps,
  ChartDataColumnChildren,
  ChartPath,
} from 'presentation/components/RightPanelModules/Chart/types';
import { findDataElementByTitle } from './utils';
import { ReportType, ReportTypes } from '@hone-automation/common';

export const createTotalColumn = (transformedData: ReportViewModel, difference: boolean = false): Column => {
  const filteredColumns = transformedData.columns.filter((column: Column) => {
    return !column.compoundColumn && column.originType !== 'Total' && column.originType !== 'Difference';
  });
  const totalRowData: Amounts[] = filteredColumns.reduce((acc: any, column: any, index: number) => {
    column.data.forEach((elem: Amounts, rowIndex: number) => {
      if (!acc[rowIndex]) {
        acc[rowIndex] = { ...elem };
        acc[rowIndex].percent = acc[rowIndex].percent || 0;
      } else {
        if (rowIndex > 0 && difference) {
          acc[rowIndex].amount -= elem.amount ? elem.amount : 0;
          acc[rowIndex].percent -= elem.percent ? elem.percent : 0;
        } else {
          acc[rowIndex].baseAmount += elem.baseAmount ? elem.baseAmount : 0;
          acc[rowIndex].percent += elem.percent ? elem.percent : 0;
          acc[rowIndex].dataRows = acc[rowIndex].dataRows.concat(elem.dataRows || []);

          acc[rowIndex].amount += elem.amount ? elem.amount : 0;
          if (index === filteredColumns.length - 1) {
            if (acc[rowIndex].percent !== 0) acc[rowIndex].percent = acc[rowIndex].amount / acc[rowIndex].baseAmount;
            acc[rowIndex].transactions = acc[rowIndex].dataRows.length;
          }
        }
      }
    });
    return acc;
  }, []);

  const totalColumn: Column = {
    name: difference ? 'Difference' : 'Total',
    date: { start: difference ? 'Δ Difference' : '∑ Total', end: '' },
    type: [{ type: 'Direct', index: 0 }],
    data: totalRowData,
    total: true,
    periodReporting: false,
    originType: 'Synthetic ' + (difference ? 'Difference' : 'Total'),
  };
  return totalColumn;
};

const rowById = (transformedData: ReportViewModel, rowId: string): any => {
  return transformedData.columns[0].data?.findIndex(row => row.id?.startsWith(rowId));
};

export const createChartsValues = (transformedData: ReportViewModel, report: NestedHoneReport): ChartObject => {
  const filteredColumns = transformedData.columns.filter((column: Column) => {
    return !column.compoundColumn && column.originType !== 'Total' && column.originType !== 'Difference';
  });
  const rowCharts: ChartObject = {};
  const rowCount = filteredColumns[0]?.data?.length || 0;

  // Calculate isLocation from columns (multi-location if any column has isLocation flag)
  const isLocation = filteredColumns.some(col => col.isLocation) || false;

  // Get consolidated status from report dates
  const isConsolidated = report.dates?.some(date => date.groupedDates && !date.location?.name) || false;

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    const title = report.type + ' (' + report.timeframe + ')';
    const children = filteredColumns[0].data?.[rowIndex].children;
    const isParentAccount = children.length > 0;

    const path: ChartPath[] = filteredColumns[0].data?.[rowIndex].path.map((pathItem: string) => {
      if (!filteredColumns[1].data) return null;
      const target = findDataElementByTitle(filteredColumns[1].data, pathItem);
      if (!target) return null;
      return { id: target.id, name: pathItem };
    });

    // Get date range from first non-account column
    const firstDataColumn = filteredColumns.find(col => col.date?.start && col.date?.end);
    const timeFrameRange = firstDataColumn?.date ? `${firstDataColumn.date.start} - ${firstDataColumn.date.end}` : '';

    const chart: ChartProps = {
      id: filteredColumns[0].data?.[rowIndex].id,
      info: title,
      name: filteredColumns[0].data?.[rowIndex].amountTitle,
      timeFrame: timeFrameRange,
      isLocation: isLocation,
      total: transformedData.columns[0].data?.[rowIndex].vizHelpers.isTotal || false,
      consolidated: isConsolidated,
      display: { money: true, percent: true },
      budget: transformedData.capabilities.budget,
      groups: ['Actual', 'Budget', 'Variance'],
      columns: [],
      isParent: isParentAccount,
      path: path,
      glCode: filteredColumns[0].data?.[rowIndex].glCode,
    };

    // let's find is it is a Parent Account and what account it is it's total
    let totalIndex = 0;
    if (isParentAccount) {
      const idparts = chart.id.split('_@_');
      const totalAccount = idparts[0] + '-total_@_';
      totalIndex = rowById(transformedData, totalAccount);
    }

    filteredColumns.forEach((column: Column, index: number) => {
      if (index === 0) return;
      const elem: Amounts = column.data?.[isParentAccount && totalIndex > -1 ? totalIndex : rowIndex] || {};
      const title =
        report.type === ReportTypes.APAging
          ? column.date?.start
          : formatRangeValues(
              column.date?.start && column.date?.end
                ? `${column.date?.start} - ${column.date?.end}`
                : column.date?.start,
              'KS'
            );

      let name = title || column.name || '';
      if (column.isLocation) {
        name = column.name || 'Location';
      }
      const col: ChartDataColumn = {
        name: name,
        liveReport: column.liveReport || false,
        data: {
          money: { actual: elem.amount || 0, budget: elem.budget || 0, variance: elem.difference || 0 },
          percent: { actual: elem.percent || 0, budget: elem.budgetPercent || 0, variance: elem.variance || 0 },
        },
      };

      if (isParentAccount) {
        const columnChildren = children
          .map((child: any) => {
            const childIndex = rowById(transformedData, child);
            if (childIndex < 0) return null;
            const childElem: Amounts = column.data?.[childIndex] || {};
            if (childElem.id.indexOf('-total_@_') > -1) return null;
            const childAccount = filteredColumns[0].data?.[childIndex];
            const hasChildren = childAccount.children?.length > 0;
            let childrenTotalIndex = 0;
            if (hasChildren) {
              const childAccountId = childAccount.id;
              const childAccountIdParts = childAccountId.split('_@_');
              const childAccountTotal = childAccountIdParts[0] + '-total_@_';
              childrenTotalIndex = rowById(transformedData, childAccountTotal);
            }
            const childCol: ChartDataColumnChildren = {
              id: childElem.id,
              glCode: childAccount.glCode,
              title: childElem.title,
              money:
                hasChildren && childrenTotalIndex > -1
                  ? column.data?.[childrenTotalIndex].amount
                  : childElem.amount || 0,
              percent:
                hasChildren && childrenTotalIndex > -1
                  ? column.data?.[childrenTotalIndex].percent
                  : childElem.percent || 0,
            };
            return childCol;
          })
          .filter(Boolean);
        col.childrenAccounts = columnChildren;

        // Ensure parent column has a meaningful magnitude for its stacked bar.
        // If the stored actual amount is zero but the children have values that
        // cancel each other out (positive and negative), set the value to the
        // sum of absolute child amounts. This prevents the rendering layer from
        // treating the chart as "all zeros" and skipping the stacked horizontal bar.
        const absChildrenTotal = columnChildren.reduce(
          (sum: number, c: ChartDataColumnChildren) => sum + Math.abs(c.money || 0),
          0
        );
        if (col.data.money.actual === 0 && absChildrenTotal !== 0) {
          col.data.money.actual = absChildrenTotal;
        }
      }
      chart.columns.push(col);
    });
    rowCharts[chart.id] = chart;
  }
  return rowCharts;
};
